// ============================================================
// 📁 آدرس این فایل در پروژه (فایل جدید):
//    src/utils/goodsReceiptReport.ts
//
// گزارش «تاریخچه‌ی توزیع بار و رسید گدام» — دو خروجی با ساختار و ظاهر یکسان:
//   • اکسل واقعی (فقط یک شیت) با فرمول‌های SUM و AVERAGE و کارت‌های خلاصه‌ی زنده
//   • PDF (از طریق چاپ مرورگر → Save as PDF)
// ============================================================

import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { gregorianToJalaliStr as _g2j, todayGregorian } from "./PersianDatePicker";

// همه‌ی اعداد سیستم انگلیسی باشند (حتی اگر تاریخ‌ساز فارسی‌رقم تولید کند)
const toEn = (s: string) => String(s ?? "").replace(/[۰-۹]/g, d => String("۰۱۲۳۴۵۶۷۸۹".indexOf(d))).replace(/[٠-٩]/g, d => String("٠١٢٣٤٥٦٧٨٩".indexOf(d)));
const gregorianToJalaliStr = (d: string) => toEn(_g2j(d));
const nowJalali = () => gregorianToJalaliStr(todayGregorian());
const nowTime = () => new Date().toLocaleTimeString("en-GB");

export interface ReceiptReportMeta {
  title?: string;           // عنوان اختیاری (مثلاً «گزارش توزیع بار خرید PUR-0012»)
  periodLabel: string;      // مثلاً «همه دوره‌ها» یا «۱۴۰۵/۰۷/۰۱ تا ۱۴۰۵/۰۷/۱۰»
  filtersLabel: string;     // خلاصه‌ی فیلترهای فعال (متن)
}

declare const process: any;
const STATUS_LABEL = (r: any) => r.type === "waste" ? "ضایعات" : r.status === "received" ? "رسید شده" : "در انتظار رسید";

const fileUrl = (path: string) => {
  if (!path) return "";
  if (/^https?:\/\//i.test(path)) return path;
  let origin = window.location.origin;
  try { if (process.env.REACT_APP_API_URL) origin = new URL(process.env.REACT_APP_API_URL).origin; } catch {}
  return origin + (path.startsWith("/") ? path : "/" + path);
};

const discText = (r: any) => {
  if (!r.discrepancy_direction) return "";
  const q = Math.abs(Number(r.discrepancy_qty_units) || 0);
  return `${r.discrepancy_direction === "shortage" ? "کسری" : "اضافه"} ${q} ${r.discrepancy_unit_name || ""}`.trim();
};

// مقدار نهایی رسیده (به واحد پایه) — فقط برای ردیف‌های رسید شده
const finalQty = (r: any) => (r.type === "receipt" && r.status === "received") ? Number(r.base_quantity) || 0 : 0;
const baseQty = (r: any) => r.type === "waste" ? 0 : Number(r.expected_base_quantity) || 0;
const wasteQty = (r: any) => r.type === "waste" ? Number(r.base_quantity) || 0 : 0;
const discBase = (r: any) => r.discrepancy_direction ? Number(r.discrepancy_base_qty) || 0 : 0;

const nf = (n: number, d = 2) => new Intl.NumberFormat("en-US", { minimumFractionDigits: 0, maximumFractionDigits: d }).format(n);

// ────────────────────────────────────────────────────────────
//  اکسل — فقط یک شیت
// ────────────────────────────────────────────────────────────
export async function generateReceiptsExcel(rows: any[], meta: ReceiptReportMeta) {
  const wb = new ExcelJS.Workbook();
  wb.creator = "سیستم حسابداری";
  wb.created = new Date();

  const ws = wb.addWorksheet("گزارش رسید جنس", {
    views: [{ rightToLeft: true, state: "frozen", ySplit: 10 }],
    pageSetup: { orientation: "landscape", fitToPage: true },
  });

  const NAVY = "1E293B", ACCENT = "D97706", GREEN = "059669", RED = "DC2626", LIGHT = "F1F5F9", WHITE = "FFFFFF", PURPLE = "7C3AED", BLUE = "2563EB";

  const cols = [
    { header: "#", key: "idx", width: 6 },
    { header: "تاریخ توزیع", key: "date", width: 13 },
    { header: "شماره خرید", key: "serial", width: 14 },
    { header: "فروشنده", key: "vendor", width: 22 },
    { header: "جنس", key: "product", width: 22 },
    { header: "دسته‌بندی", key: "category", width: 16 },
    { header: "گدام", key: "warehouse", width: 16 },
    { header: "واحد", key: "unit", width: 9 },
    { header: "مقدار (به واحد)", key: "qty_units", width: 14 },
    { header: "معادل پایه", key: "base", width: 14 },
    { header: "شماره موتر", key: "plate", width: 13 },
    { header: "شماره بارنامه", key: "waybill", width: 14 },
    { header: "وضعیت", key: "status", width: 14 },
    { header: "تاریخ رسید", key: "rdate", width: 13 },
    { header: "کسری / اضافه", key: "disc_text", width: 16 },
    { header: "کسری(−) / اضافه(+) پایه", key: "disc_base", width: 16 },
    { header: "مقدار نهایی رسیده", key: "final", width: 16 },
    { header: "ارزش ضایعات", key: "waste_value", width: 15 },
    { header: "توضیحات", key: "notes", width: 30 },
    { header: "ضمیمه", key: "att", width: 12 },
  ];
  ws.columns = cols.map(c => ({ key: c.key, width: c.width }));
  const N = cols.length;
  const L = (key: string) => ws.getColumn(key).letter;
  const fill = (argb: string) => ({ type: "pattern" as const, pattern: "solid" as const, fgColor: { argb } });

  // عنوان
  ws.mergeCells(1, 1, 2, N);
  const t = ws.getCell("A1");
  t.value = "📦  " + (meta.title || "گزارش توزیع بار و رسید گدام");
  t.font = { name: "Arial", size: 20, bold: true, color: { argb: WHITE } };
  t.alignment = { horizontal: "center", vertical: "middle" };
  ws.getRow(1).height = 26; ws.getRow(2).height = 26;
  for (let r = 1; r <= 2; r++) for (let c = 1; c <= N; c++) ws.getCell(r, c).fill = fill(NAVY);

  ws.mergeCells(3, 1, 3, N);
  const sub = ws.getCell("A3");
  sub.value = `دوره: ${meta.periodLabel}   |   فیلترها: ${meta.filtersLabel || "بدون فیلتر"}   |   تاریخ تولید گزارش: ${nowJalali()}`;
  sub.font = { name: "Arial", size: 11, color: { argb: WHITE } };
  sub.alignment = { horizontal: "center" };
  sub.fill = fill(ACCENT);
  ws.getRow(3).height = 20;
  ws.getRow(4).height = 8;

  // محدوده‌ی داده (ردیف‌ها از ۱۱ شروع می‌شود)
  const first = 11;
  const last = first + Math.max(rows.length, 1) - 1;
  const rng = (k: string) => `${L(k)}${first}:${L(k)}${last}`;

  // کارت‌های خلاصه (فرمول زنده)
  const val = {
    cnt: rows.length,
    dist: rows.reduce((s, r) => s + baseQty(r), 0),
    recv: rows.reduce((s, r) => s + finalQty(r), 0),
    short: rows.reduce((s, r) => s + (discBase(r) < 0 ? -discBase(r) : 0), 0),
    over: rows.reduce((s, r) => s + (discBase(r) > 0 ? discBase(r) : 0), 0),
    waste: rows.reduce((s, r) => s + wasteQty(r), 0),
  };
  const cards: [string, any, string, string][] = [
    ["🔢 تعداد ردیف‌ها", { formula: `COUNT(${rng("idx")})`, result: val.cnt }, PURPLE, "#,##0"],
    ["🚚 جمع بار توزیع‌شده", { formula: `SUM(${rng("base")})`, result: val.dist }, BLUE, "#,##0.###"],
    ["✅ جمع رسیده به گدام", { formula: `SUM(${rng("final")})`, result: val.recv }, GREEN, "#,##0.###"],
    ["⚠ جمع کسری", { formula: `-SUMIF(${rng("disc_base")},"<0")`, result: val.short }, RED, "#,##0.###"],
    ["➕ جمع اضافه", { formula: `SUMIF(${rng("disc_base")},">0")`, result: val.over }, GREEN, "#,##0.###"],
    ["🗑 ضایعات (مقدار)", { formula: `SUM(${L2(N + 1)}${first}:${L2(N + 1)}${last})`, result: val.waste }, "9A3412", "#,##0.###"],
  ];
  // ستون پنهان کمکی: مقدار ضایعات هر ردیف (تا کارت ضایعات هم فرمول زنده باشد)
  ws.getColumn(N + 1).width = 2; ws.getColumn(N + 1).hidden = true;

  const span = Math.floor(N / cards.length);
  let col = 1;
  cards.forEach(([label, v, color, fmtStr], i) => {
    const s = col, e = i === cards.length - 1 ? N : col + span - 1;
    ws.mergeCells(5, s, 5, e); ws.mergeCells(6, s, 6, e);
    const lc = ws.getCell(5, s); lc.value = label;
    lc.font = { name: "Arial", size: 10, bold: true, color: { argb: "64748B" } };
    lc.alignment = { horizontal: "center" };
    const vc = ws.getCell(6, s); vc.value = v;
    vc.numFmt = fmtStr;
    vc.font = { name: "Arial", size: 14, bold: true, color: { argb: color } };
    vc.alignment = { horizontal: "center" };
    for (let r = 5; r <= 6; r++) for (let c = s; c <= e; c++) {
      ws.getCell(r, c).fill = fill(LIGHT);
      ws.getCell(r, c).border = {
        top: { style: "thin", color: { argb: "E2E8F0" } }, bottom: { style: "thin", color: { argb: "E2E8F0" } },
        left: { style: c === s ? "medium" : "thin", color: { argb: c === s ? color : "E2E8F0" } },
        right: { style: "thin", color: { argb: "E2E8F0" } },
      };
    }
    col = e + 1;
  });
  ws.getRow(5).height = 17; ws.getRow(6).height = 22;
  ws.getRow(7).height = 6;

  // عنوان جدول
  ws.mergeCells(8, 1, 8, N);
  const tt = ws.getCell("A8");
  tt.value = "📋  جزئیات کامل توزیع و رسید";
  tt.font = { name: "Arial", size: 12.5, bold: true, color: { argb: NAVY } };
  tt.border = { bottom: { style: "medium", color: { argb: ACCENT } } };
  ws.getRow(8).height = 20;
  ws.getRow(9).height = 4;

  // هدر
  const hr = ws.getRow(10);
  cols.forEach((c, i) => {
    const cell = hr.getCell(i + 1);
    cell.value = c.header;
    cell.font = { name: "Arial", size: 10.5, bold: true, color: { argb: WHITE } };
    cell.fill = fill(NAVY);
    cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
    cell.border = { top: { style: "thin" }, bottom: { style: "medium", color: { argb: ACCENT } }, left: { style: "thin", color: { argb: "334155" } }, right: { style: "thin", color: { argb: "334155" } } };
  });
  hr.height = 30;

  // ردیف‌ها
  const hiddenCol = N + 1;
  if (rows.length === 0) {
    ws.getRow(first).getCell(1).value = "هیچ ردیفی یافت نشد";
    ws.mergeCells(first, 1, first, N);
    ws.getRow(first).getCell(1).alignment = { horizontal: "center" };
  }
  rows.forEach((r, i) => {
    const row = ws.getRow(first + i);
    const vals: Record<string, any> = {
      idx: i + 1,
      date: gregorianToJalaliStr(r.date),
      serial: r.purchase_serial || "",
      vendor: r.vendor_name || "",
      product: r.product_name || "",
      category: r.category_name || "",
      warehouse: r.warehouse_name || "",
      unit: r.unit_name || "",
      qty_units: Number(r.quantity_units) || 0,
      base: baseQty(r),
      plate: r.truck_plate || "",
      waybill: r.waybill_number || "",
      status: STATUS_LABEL(r),
      rdate: r.received_date ? gregorianToJalaliStr(r.received_date) : "",
      disc_text: discText(r),
      disc_base: discBase(r),
      final: finalQty(r),
      waste_value: r.type === "waste" ? Number(r.waste_value) || 0 : 0,
      notes: [r.notes, r.receipt_notes].filter(Boolean).join(" | "),
      att: "",
    };
    cols.forEach((c, ci) => { row.getCell(ci + 1).value = vals[c.key]; });
    row.getCell(hiddenCol).value = wasteQty(r);
    const link = r.receipt_attachment || r.attachment;
    if (link) {
      row.getCell(cols.findIndex(c => c.key === "att") + 1).value = { text: "مشاهده", hyperlink: fileUrl(link) } as any;
    }
    const even = i % 2 === 0;
    for (let c = 1; c <= N; c++) {
      const cell = row.getCell(c);
      cell.fill = fill(even ? WHITE : LIGHT);
      cell.font = { name: "Arial", size: 10, color: { argb: "1E293B" } };
      cell.border = { bottom: { style: "hair", color: { argb: "E2E8F0" } } };
      cell.alignment = { vertical: "middle", horizontal: ["vendor", "product", "category", "notes"].includes(cols[c - 1].key) ? "right" : "center", wrapText: cols[c - 1].key === "notes" };
    }
    ["qty_units", "base", "disc_base", "final"].forEach(k => { row.getCell(L(k)).numFmt = "#,##0.###"; });
    row.getCell(L("waste_value")).numFmt = "#,##0.00";
    const st = vals.status;
    row.getCell(L("status")).font = { name: "Arial", size: 10, bold: true, color: { argb: st === "رسید شده" ? GREEN : st === "ضایعات" ? "9A3412" : ACCENT } };
    if (vals.disc_base < 0) row.getCell(L("disc_base")).font = { name: "Arial", size: 10, bold: true, color: { argb: RED } };
    if (vals.disc_base > 0) row.getCell(L("disc_base")).font = { name: "Arial", size: 10, bold: true, color: { argb: GREEN } };
    if (link) row.getCell(L("att")).font = { name: "Arial", size: 10, bold: true, color: { argb: BLUE }, underline: true };
  });

  // جمع کل و میانگین (فرمول واقعی)
  const sumRowN = last + 1, avgRowN = last + 2;
  const numKeys = ["qty_units", "base", "disc_base", "final", "waste_value"];
  const mkTotal = (rowN: number, label: string, fn: "SUM" | "AVERAGE", bg: string) => {
    const row = ws.getRow(rowN);
    ws.mergeCells(rowN, 1, rowN, cols.findIndex(c => c.key === "qty_units"));
    row.getCell(1).value = label;
    numKeys.forEach(k => {
      const cell = row.getCell(L(k));
      const data = rows.map(r => k === "qty_units" ? Number(r.quantity_units) || 0 : k === "base" ? baseQty(r) : k === "disc_base" ? discBase(r) : k === "final" ? finalQty(r) : (r.type === "waste" ? Number(r.waste_value) || 0 : 0));
      const total = data.reduce((s, x) => s + x, 0);
      const result = fn === "SUM" ? total : (data.length ? total / data.length : 0);
      cell.value = { formula: `${fn}(${rng(k)})`, result };
      cell.numFmt = k === "waste_value" ? "#,##0.00" : "#,##0.###";
    });
    for (let c = 1; c <= N; c++) {
      const cell = row.getCell(c);
      cell.font = { name: "Arial", size: 11, bold: true, color: { argb: NAVY } };
      cell.fill = fill(bg);
      cell.alignment = { horizontal: "center", vertical: "middle" };
      cell.border = { top: { style: "double", color: { argb: NAVY } } };
    }
    row.height = 22;
  };
  if (rows.length > 0) {
    mkTotal(sumRowN, "جمع کل", "SUM", "FEF3C7");
    mkTotal(avgRowN, "میانگین", "AVERAGE", "E0F2FE");
  }

  ws.addRow([]);
  const foot = ws.addRow(["این گزارش به‌صورت سیستمی تولید شده است. ستون «ضمیمه» به فایل/صدای ثبت‌شده لینک دارد. مقادیر «پایه» بر اساس واحد پایه‌ی هر جنس است."]);
  ws.mergeCells(foot.number, 1, foot.number, N);
  foot.getCell(1).font = { name: "Arial", size: 9, italic: true, color: { argb: "94A3B8" } };
  foot.getCell(1).alignment = { horizontal: "center" };

  const buf = await wb.xlsx.writeBuffer();
  saveAs(new Blob([buf], { type: "application/octet-stream" }), `گزارش-رسید-جنس-${new Date().toISOString().slice(0, 10)}.xlsx`);
}

// حرف ستون اکسل از شماره (برای ستون کمکی)
function L2(n: number) {
  let s = ""; while (n > 0) { const m = (n - 1) % 26; s = String.fromCharCode(65 + m) + s; n = Math.floor((n - 1) / 26); }
  return s;
}

// ────────────────────────────────────────────────────────────
//  PDF (چاپ مرورگر)
// ────────────────────────────────────────────────────────────
export function printReceiptsPdf(rows: any[], meta: ReceiptReportMeta) {
  const w = window.open("", "_blank");
  if (!w) return false;
  const color = "#d97706";
  const sum = (f: (r: any) => number) => rows.reduce((s, r) => s + f(r), 0);
  const tDist = sum(baseQty), tRecv = sum(finalQty);
  const tShort = sum(r => discBase(r) < 0 ? -discBase(r) : 0), tOver = sum(r => discBase(r) > 0 ? discBase(r) : 0);
  const tWaste = sum(wasteQty), tWasteVal = sum(r => r.type === "waste" ? Number(r.waste_value) || 0 : 0);
  const n = rows.length || 1;

  const body = rows.length === 0
    ? `<tr><td colspan="16" style="text-align:center;padding:40px;color:#94a3b8;font-size:13px">هیچ ردیفی یافت نشد</td></tr>`
    : rows.map((r, i) => {
        const st = STATUS_LABEL(r);
        const stColor = st === "رسید شده" ? "#059669" : st === "ضایعات" ? "#9a3412" : "#d97706";
        const db = discBase(r);
        const link = r.receipt_attachment || r.attachment;
        return `<tr class="${i % 2 === 0 ? "row-even" : "row-odd"}">
          <td class="c" style="color:#94a3b8">${i + 1}</td>
          <td class="c">${gregorianToJalaliStr(r.date)}</td>
          <td class="c" style="font-weight:700;color:#d97706">${r.purchase_serial || "—"}</td>
          <td>${r.vendor_name || "—"}</td>
          <td style="font-weight:700">${r.product_name || "—"}${r.category_name ? `<div style="font-weight:400;font-size:9px;color:#64748b">دسته: ${r.category_name}</div>` : ""}</td>
          <td>${r.warehouse_name || "—"}</td>
          <td class="c">${nf(Number(r.quantity_units) || 0, 3)} ${r.unit_name || ""}</td>
          <td class="c">${nf(baseQty(r), 3)}</td>
          <td class="c">${r.truck_plate || "—"}</td>
          <td class="c">${r.waybill_number || "—"}</td>
          <td class="c"><span style="background:${stColor}18;color:${stColor};padding:2px 8px;border-radius:6px;font-weight:700;white-space:nowrap">${st}</span></td>
          <td class="c">${r.received_date ? gregorianToJalaliStr(r.received_date) : "—"}</td>
          <td class="c" style="font-weight:700;color:${db < 0 ? "#dc2626" : db > 0 ? "#059669" : "#64748b"}">${discText(r) || "—"}</td>
          <td class="c" style="font-weight:800">${r.type === "waste" ? nf(wasteQty(r), 3) + " ⟵ ضایعات" : finalQty(r) ? nf(finalQty(r), 3) : "—"}</td>
          <td style="font-size:9.5px;color:#475569;max-width:170px">${[r.notes, r.receipt_notes].filter(Boolean).join(" | ") || "—"}</td>
          <td class="c">${link ? `<a href="${fileUrl(link)}" target="_blank" style="color:#2563eb;font-weight:700">مشاهده</a>` : "—"}</td>
        </tr>`;
      }).join("");

  w.document.write(`<!DOCTYPE html>
<html dir="rtl" lang="fa"><head><meta charset="UTF-8">
<title>${meta.title || "گزارش رسید جنس"}</title>
<link href="https://fonts.googleapis.com/css2?family=Vazirmatn:wght@300;400;600;700;800&display=swap" rel="stylesheet">
<style>
*,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
@page{size:A4 landscape;margin:10mm 12mm}
body{font-family:'Vazirmatn',Tahoma,sans-serif;direction:rtl;font-size:11px;color:#1e293b;background:#fff;-webkit-print-color-adjust:exact;print-color-adjust:exact}
.page{padding:6px}
.header{position:relative;display:flex;justify-content:space-between;align-items:center;padding:18px 22px;margin-bottom:18px;border-radius:14px;background:linear-gradient(120deg,${color} 0%,${color}cc 55%,${color}99 100%);box-shadow:0 6px 18px ${color}33;overflow:hidden}
.header::after{content:"";position:absolute;inset:0;background:radial-gradient(circle at 85% -20%,rgba(255,255,255,.22),transparent 55%)}
.hr{position:relative;z-index:1;display:flex;align-items:center;gap:14px}
.logo{width:48px;height:48px;background:rgba(255,255,255,.18);border:1px solid rgba(255,255,255,.35);border-radius:13px;display:flex;align-items:center;justify-content:center;font-size:23px}
.ht{font-size:21px;font-weight:800;color:#fff}.hs{font-size:11px;color:rgba(255,255,255,.88);margin-top:3px}
.hl{position:relative;z-index:1;text-align:left}
.badge{display:inline-block;background:rgba(255,255,255,.95);color:${color};font-size:10px;font-weight:800;padding:4px 14px;border-radius:20px;margin-bottom:5px}
.pd{font-size:10px;color:rgba(255,255,255,.85)}
.cards{display:flex;gap:10px;margin-bottom:16px;flex-wrap:wrap}
.card{flex:1;min-width:120px;border-radius:12px;padding:10px 13px;box-shadow:0 2px 6px rgba(15,23,42,.05);position:relative;background:#fff;border:1px solid #e7eaf0}
.card::before{content:"";position:absolute;top:10px;bottom:10px;right:0;width:4px;border-radius:3px;background:var(--c)}
.cl{font-size:9.5px;font-weight:700;color:#64748b;margin-bottom:4px}.cv{font-size:15px;font-weight:800;color:var(--c)}
.tt{font-size:12px;font-weight:800;color:#374151;margin-bottom:9px;padding-right:9px;border-right:3px solid ${color}}
.tw{border-radius:12px;overflow:hidden;box-shadow:0 4px 14px rgba(15,23,42,.08);border:1px solid #e7eaf0}
table{width:100%;border-collapse:collapse;font-size:10px}
thead tr{background:linear-gradient(120deg,#1e293b,#334155 60%,${color}cc)}
th{padding:9px 6px;text-align:center;font-weight:700;color:#f1f5f9;font-size:9.5px}
.row-even{background:#fff}.row-odd{background:#f8fafc}
td{padding:7px 6px;border-bottom:1px solid #f1f5f9;vertical-align:middle}
td.c{text-align:center}
tfoot td{background:#fef3c7;font-weight:800;padding:9px 6px;border-top:2px double #1e293b;text-align:center}
tfoot tr.avg td{background:#e0f2fe}
.footer{margin-top:22px;padding-top:14px;border-top:1px dashed #d8dee8;display:flex;justify-content:space-between;align-items:flex-end}
.fn{font-size:9.5px;color:#94a3b8;max-width:60%}.fs{font-size:10px;color:#64748b;border-top:1px solid #cbd5e1;padding-top:6px;min-width:170px;text-align:center}
@media print{thead{display:table-header-group}tr{page-break-inside:avoid}.header,.card,.tw{box-shadow:none!important}}
</style></head><body><div class="page">
<div class="header">
  <div class="hr"><div class="logo">📦</div><div><div class="ht">${meta.title || "گزارش توزیع بار و رسید گدام"}</div><div class="hs">تاریخچه‌ی کامل توزیع، رسید، کسری/اضافه و ضایعات</div></div></div>
  <div class="hl"><div><span class="badge">دوره: ${meta.periodLabel}</span></div>
  <div class="pd">تاریخ چاپ: ${nowJalali()} — ${nowTime()}</div>
  ${meta.filtersLabel ? `<div class="pd" style="margin-top:2px">🔎 ${meta.filtersLabel}</div>` : ""}</div>
</div>
<div class="cards">
  <div class="card" style="--c:#7c3aed"><div class="cl">🔢 تعداد ردیف‌ها</div><div class="cv">${rows.length}</div></div>
  <div class="card" style="--c:#2563eb"><div class="cl">🚚 جمع بار توزیع‌شده</div><div class="cv">${nf(tDist, 3)}</div></div>
  <div class="card" style="--c:#059669"><div class="cl">✅ جمع رسیده به گدام</div><div class="cv">${nf(tRecv, 3)}</div></div>
  <div class="card" style="--c:#dc2626"><div class="cl">⚠ جمع کسری</div><div class="cv">${nf(tShort, 3)}</div></div>
  <div class="card" style="--c:#16a34a"><div class="cl">➕ جمع اضافه</div><div class="cv">${nf(tOver, 3)}</div></div>
  <div class="card" style="--c:#9a3412"><div class="cl">🗑 ضایعات</div><div class="cv">${nf(tWaste, 3)}</div></div>
</div>
<div class="tt">جزئیات کامل توزیع و رسید</div>
<div class="tw"><table>
<thead><tr><th>#</th><th>تاریخ توزیع</th><th>شماره خرید</th><th>فروشنده</th><th>جنس</th><th>گدام</th><th>مقدار</th><th>معادل پایه</th><th>موتر</th><th>بارنامه</th><th>وضعیت</th><th>تاریخ رسید</th><th>کسری/اضافه</th><th>نهایی رسیده</th><th>توضیحات</th><th>ضمیمه</th></tr></thead>
<tbody>${body}</tbody>
${rows.length ? `<tfoot>
<tr><td colspan="7">جمع کل</td><td>${nf(tDist, 3)}</td><td colspan="4"></td><td>${nf(sum(discBase), 3)}</td><td>${nf(tRecv, 3)}</td><td colspan="2">${tWasteVal ? "ارزش ضایعات: " + nf(tWasteVal, 2) : ""}</td></tr>
<tr class="avg"><td colspan="7">میانگین هر ردیف</td><td>${nf(tDist / n, 3)}</td><td colspan="4"></td><td>${nf(sum(discBase) / n, 3)}</td><td>${nf(tRecv / n, 3)}</td><td colspan="2"></td></tr>
</tfoot>` : ""}
</table></div>
<div class="footer"><div class="fn">این گزارش به صورت سیستمی تولید شده و نیازی به امضا ندارد. مقادیر «پایه» بر اساس واحد پایه‌ی هر جنس است.</div><div class="fs">امضا و مهر مسئول گدام</div></div>
</div></body></html>`);
  w.document.close();
  setTimeout(() => w.print(), 700);
  return true;
}
